import { useCallback, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';
import { fetchNetPosition } from '@/api/net-position/fetch-net-position';
import { NetPositionResponseType } from '@/api/net-position/types';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { useEnableNetPositionFeature } from './useEnableNetPositionFeature';

type NetPositionQueueItemProps = {
  spinStartedDate: number;
  receivedDate?: number | null;
  uuid: string;
  status: 'pending' | 'fulfilled' | 'rejected';
  data?: NetPositionResponseType;
};

const MAX_ERROR_LENGTH = 3;
const MAX_QUEUE_LENGTH = 6;
const INTERVAL_MS = 5000;

const logNetPosition = (
  description: string,
  statusCode: string,
  eventType: DdEventType = DdEventType.Action,
  extra: Record<string, unknown> = {},
) => {
  const event = {
    name: 'net_position',
    description,
    useCase: DdEventCategory.UserInteraction,
    eventType,
    context: { type: 'NET_POSITION', statusCode, ...extra },
  };

  if (eventType === DdEventType.Error) {
    Logger.logRumError(event);
  } else {
    Logger.logRumAction(event);
  }
};

/**
 * Maintains a local queue of net-position requests, derives the latest
 * fulfilled value, and computes lock / redirect conditions from error
 * thresholds (lock after {@link MAX_ERROR_LENGTH} non-fulfilled, prune at
 * {@link MAX_QUEUE_LENGTH}). The queue stays component-local; only the
 * resulting lock state is mirrored into the store by the consumer.
 */
export const useNetPositionQueue = () => {
  const [queue, setQueue] = useState<NetPositionQueueItemProps[]>([]);
  const [lockUI, setLockUI] = useState<boolean>(false);
  const [redirectToLobby, setShouldRedirect] = useState<boolean>(false);
  const [isIntervalMethod, setIsIntervalMethod] = useState<boolean>(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const gameUid = useStore((s) => s.gameContext.gameUid);
  const previousGameUidRef = useRef(gameUid);
  const showGameWindow = useStore((s) => s.commonUIContext.showGameWindow);
  const {
    spinInProgress: isSpinInProgress,
    spinEndCount,
    gameLoaded,
  } = useStore(
    useShallow((s) => ({
      spinInProgress: s.gameWindowContext.spinInProgress,
      spinEndCount: s.gameWindowContext.spinEndCount,
      gameLoaded: s.gameStatus.gameLoadSuccess,
    })),
  );
  const { netPositionFeatureEnabled } = useEnableNetPositionFeature();

  const lockCheck = useCallback(() => {
    const [first, ...rest] = queue;

    const needsLock = (() => {
      if (first?.status === 'fulfilled') {
        return (
          rest.filter((item) => item.status !== 'fulfilled').length >=
          MAX_ERROR_LENGTH
        );
      }

      return (
        queue.filter((item) => item.status !== 'fulfilled').length >=
        MAX_ERROR_LENGTH
      );
    })();

    setLockUI(needsLock);
  }, [queue]);

  const updateLatestNetPosition = useCallback(async () => {
    setQueue((prev) =>
      [...prev]
        .reverse()
        .reduce<NetPositionQueueItemProps[]>((acc, item) => {
          if (acc.some((position) => position.status === 'fulfilled')) {
            return acc;
          }

          return [...acc, item];
        }, [])
        .reverse(),
    );
  }, []);

  const previousPendingCheck = useCallback(async () => {
    const [last, ...list] = [...queue].reverse();

    const moreThanOneFulfilled =
      list.filter((item) => item.status === 'fulfilled').length > 1;

    if (moreThanOneFulfilled) {
      await updateLatestNetPosition();
    }

    if (
      last?.status === 'pending' &&
      list.some((item) => item.status === 'pending')
    ) {
      logNetPosition(
        'Spin started before receiving net position of previous spin',
        'NP-201',
      );
    }
  }, [queue, updateLatestNetPosition]);

  const flushQueue = useCallback(async () => {
    setQueue((prev) => {
      const [last, ...rest] = [...prev].reverse();

      if (rest.length === 0) return prev;
      return last ? [last] : prev;
    });
  }, []);

  const checkRedirectError = useCallback(() => {
    if (queue.length < MAX_ERROR_LENGTH) {
      return;
    }

    const listToCheck = [...queue].slice(-MAX_ERROR_LENGTH);
    const isFailed = listToCheck.every((item) => item.status === 'rejected');

    setShouldRedirect(isFailed);
  }, [queue]);

  const checkQueue = useCallback(() => {
    if (queue.length === 0) {
      return;
    }

    const lastAdded = queue[queue.length - 1];

    if (lastAdded.status === 'fulfilled') {
      flushQueue();
    }

    lockCheck();
    previousPendingCheck();
    checkRedirectError();
  }, [flushQueue, lockCheck, previousPendingCheck, checkRedirectError, queue]);

  const insertInQueue = useCallback((response: NetPositionQueueItemProps) => {
    setQueue((prevQueue) => {
      if (
        prevQueue[0] &&
        prevQueue[0].uuid &&
        prevQueue[0].uuid !== response.uuid &&
        prevQueue[0].spinStartedDate &&
        prevQueue[0].spinStartedDate > response.spinStartedDate
      ) {
        logNetPosition(
          'Received net position older than previously displayed',
          'NP-202',
        );
        return prevQueue;
      }

      const atIndex = prevQueue.findIndex((n) => n.uuid === response.uuid);

      if (atIndex === -1) {
        return prevQueue;
      }

      const newQueue = [...prevQueue];
      newQueue[atIndex] = response;

      return newQueue;
    });
  }, []);

  const getNetPosition = useCallback(
    (uuid: string, spinStartedDate: number) => {
      fetchNetPosition(gameUid ?? '')
        .then((response) => {
          if (!response || response.status >= 400) {
            throw new Error(`Failed with status code ${response?.status ?? 0}`);
          }

          const receivedDate = Date.now();

          insertInQueue({
            data: response.data,
            status: 'fulfilled',
            uuid,
            spinStartedDate,
            receivedDate,
          });
        })
        .catch(() => {
          insertInQueue({
            data: {} as NetPositionResponseType,
            status: 'rejected',
            uuid,
            spinStartedDate,
          });
        });
    },
    [gameUid, insertInQueue],
  );

  const prepareNetPosition = useCallback(() => {
    const uuid = crypto.randomUUID();
    const spinStartedDate = Date.now();

    setQueue((prev) => {
      if (prev.length >= MAX_QUEUE_LENGTH) {
        const newQueue = [...prev].filter((item) => item.status === 'rejected');

        logNetPosition(
          '[WEB] Queue max length exceeded, filtering failing',
          'NP-500',
          DdEventType.Error,
          { queue: JSON.stringify(prev) },
        );

        return [
          ...newQueue,
          { uuid, status: 'pending' as const, spinStartedDate },
        ];
      }

      return [...prev, { uuid, status: 'pending' as const, spinStartedDate }];
    });

    getNetPosition(uuid, spinStartedDate);
  }, [getNetPosition]);

  useEffect(() => {
    if (previousGameUidRef.current === gameUid) {
      return;
    }

    previousGameUidRef.current = gameUid;

    setQueue([]);
    setLockUI(false);
    setShouldRedirect(false);
  }, [gameUid]);

  useEffect(() => {
    if (!isIntervalMethod && gameLoaded && !isSpinInProgress) {
      // This effect intentionally advances the queue after the game lifecycle changes.
      prepareNetPosition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntervalMethod, spinEndCount, isSpinInProgress, gameLoaded]);

  useEffect(() => {
    if (!showGameWindow && netPositionFeatureEnabled && !lockUI) {
      // The interval mode mirrors whether the game window is currently hidden.
      setIsIntervalMethod(true);
      intervalRef.current = setInterval(() => {
        prepareNetPosition();
      }, INTERVAL_MS);
    }

    if (showGameWindow) {
      setIsIntervalMethod(false);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGameWindow, netPositionFeatureEnabled, lockUI]);

  useEffect(() => {
    if (lockUI && intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, [lockUI]);

  useEffect(() => {
    // Queue transitions are consolidated here so lock/redirect state is atomic.
    checkQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  if (queue.length === 0) {
    return { netPosition: 0 };
  }

  if (!queue[0]?.data?.netPosition) {
    return {
      netPosition: 0,
      shouldLock: lockUI,
      redirectToLobby,
      redirectErrorCode: 'NP-003',
    };
  }

  return {
    netPosition: queue[0].data.netPosition,
    shouldLock: lockUI,
    redirectToLobby,
    redirectErrorCode: 'NP-003',
  };
};
